// LandPrepPopup.jsx

import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  Grid,
  InputLabel,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import DatePicker from "react-datepicker";
import CustomButton from "../../Custom/CustomButton";
import instance from "../../../globals/axios";
import { getFarmerPlotId } from "../../../globals/utils";
import { useAlert } from "../../Alert";
import EditIcon from "../../../assets/svg/EditIcon.svg";
import SuccessPopup from "./SuccessPopup";

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    fontSize: 16,
  },
  dialogContent: {
    height: 400,
  },
  datepicker: {
    display: "none",
  },
  taxtLabel: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "400 !important",
    color: "#000",
    marginTop: "10px",
  },
  taxtTitle: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "700 !important",
    color: "#000",
  },
}));

const IrrigationPopup = ({ onClose, t }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [formData, setFormData] = useState({});
  const [comment, setComment] = useState("");
  const [firstIrrigationDate, setFirstIrrigationDate] = useState(null);
  const [lastIrrigationDate, setLastIrrigationDate] = useState(null);
  const [existingData, setExistingData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const { showAlert } = useAlert();
  const [irrigationOptions, setIrrigationOptions] = useState([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [errors, setErrors] = useState({
    "type of irrigation system": "",
    firstIrrigationDate: "",
    lastIrrigationDate: "",
  });
  let farmerPlotId = getFarmerPlotId();
  const scrollableContainerStyle = {
    maxHeight: "300px", // Adjust the height as needed
    overflowY: "auto",
  };

  const rows = [
    {
      label: t("Type of irrigation system"),
      type: "dropdown",
      options: irrigationOptions,
      key: "Type of irrigation system",
    },
    {
      label: t("Date of First Irrigation"),
      type: "date",
      key: "Date of First Irrigation",
    },
    {
      label: t("Date of Last Irrigation"),
      type: "date",
      key: "Date of Last Irrigation",
    },
  ];

  useEffect(() => {
    const fetchChemicalOptions = async () => {
      try {
        const chemicalResponse = await instance.get(
          "plotengagement/get-irrigation-types"
        );
        const chemicalOptions = chemicalResponse.data.map(
          (chemical) => chemical.name
        );

        setIrrigationOptions(chemicalOptions);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchChemicalOptions();
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await instance(
          `plotengagement/get-irrigation-schedule?farmer_plot=${farmerPlotId}`
        );
        let data = response.data;
        if (data.length > 0) {
          setExistingData(data[0]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (farmerPlotId) {
      fetchData();
    }
  }, [farmerPlotId]);
  useEffect(() => {
    if (existingData) {
      setFormData({
        "type of irrigation system": existingData.irrigation_type || "",
      });

      setFirstIrrigationDate(
        existingData.date_of_first_irrigation
          ? new Date(existingData.date_of_first_irrigation)
          : null
      );
      setLastIrrigationDate(
        existingData.date_of_last_irrigation
          ? new Date(existingData.date_of_last_irrigation)
          : null
      );
      setComment(existingData.comments || "");
    }
  }, [existingData]);

  const handleDateChange = (date, field) => {
    if (field === "selectedDate") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        selectedDate: "",
      }));
    } else if (field === "firstIrrigationDate") {
      setFirstIrrigationDate(date);
      setErrors((prevErrors) => ({
        ...prevErrors,
        firstIrrigationDate: "",
      }));
    } else if (field === "lastIrrigationDate") {
      setLastIrrigationDate(date);
      setErrors((prevErrors) => ({
        ...prevErrors,
        lastIrrigationDate: "",
      }));
    }
  };
  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };
  const handleEditClick = () => {
    setIsEditMode(true);
  };
  const handleChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
    if (field === "type of irrigation system") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        ["type of irrigation system"]: value
          ? ""
          : "Please select a type of irrigation system.",
      }));
    }
  };

  const validateForm = () => {
    let formValid = true;
    let newErrors = {};

    // Clear previous errors
    setErrors({
      "type of irrigation system": "",
      firstIrrigationDate: "",
      lastIrrigationDate: "",
    });

    // Validation checks
    if (!formData["type of irrigation system"]) {
      newErrors = {
        ...newErrors,
        "type of irrigation system":
          "Please select a type of irrigation system.",
      };
      formValid = false;
    }

    if (!firstIrrigationDate) {
      newErrors = {
        ...newErrors,
        firstIrrigationDate: "Please select the first irrigation date.",
      };
      formValid = false;
    }

    // Check for the last irrigation date only if it's provided
    if (lastIrrigationDate && !firstIrrigationDate) {
      newErrors = {
        ...newErrors,
        lastIrrigationDate: "Please select the first irrigation date first.",
      };
      formValid = false;
    }

    // Update errors state
    setErrors((prevErrors) => ({
      ...prevErrors,
      ...newErrors,
    }));

    return formValid;
  };

  const handleSubmit = () => {
    const formIsValid = validateForm();
    if (formIsValid) {
      const formatDate = (date) => {
        const pad = (num) => (num < 10 ? `0${num}` : `${num}`);
        return date
          ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
              date.getDate()
            )}`
          : null;
      };
      const firstIrrigationDated = formatDate(firstIrrigationDate);
      const lastIrrigationDated = formatDate(lastIrrigationDate);
      const formattedFormData = {
        irrigation_type: formData["type of irrigation system"],
        date_of_first_irrigation: firstIrrigationDated
          ? firstIrrigationDated
          : null,
        date_of_last_irrigation: lastIrrigationDated
          ? lastIrrigationDated
          : null,

        comments: comment,
      };

      if (isEditMode) {
        formattedFormData.id = existingData.id;
      } else {
        formattedFormData.farmer_plot = farmerPlotId;
      }
      const apiUrl = isEditMode
        ? "plotengagement/update-irrigation-schedule"
        : "plotengagement/create-irrigation-schedule";
      instance
        .post(apiUrl, formattedFormData)
        .then((res) => {
          if (res && res.data) {
            setShowSuccessPopup(true);
            setTimeout(() => {
              setShowSuccessPopup(false);
              onClose();
            }, 2000);
          }
        })
        .catch((error) => {
          if (
            error.response &&
            error.response.data &&
            error.response.data.non_field_errors
          ) {
            const errorMessage = error.response.data.non_field_errors[0];
            showAlert("error", errorMessage);
          } else {
            showAlert("error", "Some Error Occurred");
          }
        });
    }
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        style={{ zIndex: 1 }}
        PaperProps={{ style: { maxHeight: "calc(100% - 25%)" } }}
      >
        <div style={{ padding: "16px" }}>
          <Grid container>
            <Grid item xs={10}>
              <DialogTitle style={{ padding: "0px" }}>
                {t("Irrigation Schedule")}
              </DialogTitle>
            </Grid>
            {existingData !== null ? (
              <Grid item xs={1} style={{ marginTop: "10px" }}>
                <img src={EditIcon} onClick={handleEditClick} />
              </Grid>
            ) : null}
            <Grid item xs={1}>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>

          <div style={{ ...scrollableContainerStyle }}>
            {rows.map((row, index) => (
              <Grid key={index} container style={{ marginTop: "10px" }}>
                <Grid
                  item
                  xs={
                    typeof row.options !== "undefined" &&
                    typeof row.options[0] === "number"
                      ? 9
                      : 7
                  }
                >
                  {row.type === "date" ? (
                    <InputLabel className={classes.taxtLabel}>
                      {row.label}
                    </InputLabel>
                  ) : (
                    <InputLabel style={{ fontWeight: 700, fontSize: "12px" }}>
                      {t(row.label)}
                    </InputLabel>
                  )}
                </Grid>
                <Grid
                  item
                  xs={
                    typeof row.options !== "undefined" &&
                    typeof row.options[0] === "number"
                      ? 3
                      : 5
                  }
                >
                  {row.type === "dropdown" ? (
                    <Select
                      value={formData[row.key.toLowerCase()] || ""}
                      disabled={!isEditMode && existingData !== null}
                      onChange={(event) =>
                        handleChange(row.key.toLowerCase(), event.target.value)
                      } // Using row.key instead of row.label
                      style={{
                        width:
                          typeof row.options !== "undefined" &&
                          typeof row.options[0] === "number"
                            ? "60px"
                            : typeof row.options[0] === "string"
                            ? "100px"
                            : "100%",
                      }}
                      error={Boolean(errors["type of irrigation system"])} // Ensure error is triggered based on the error state
                      helperText={errors["type of irrigation system"]}
                      sx={{
                        height: "40px",
                        border: "1px solid #9C9FA0",
                        borderRadius: "5px",
                      }}
                    >
                      {row.options.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : row.type === "date" ? (
                    <Grid container>
                      <Grid item xs={10}>
                        <TextField
                          placeholder="DD/MM/YYYY"
                          disabled={!isEditMode && existingData !== null}
                          fullWidth
                          value={
                            row.key === "Date of First Irrigation"
                              ? firstIrrigationDate
                                ? firstIrrigationDate.toLocaleDateString()
                                : ""
                              : lastIrrigationDate
                              ? lastIrrigationDate.toLocaleDateString()
                              : ""
                          }
                          error={
                            !!errors[
                              row.key === "Date of First Irrigation"
                                ? "firstIrrigationDate"
                                : "lastIrrigationDate"
                            ]
                          }
                          helperText={
                            errors[
                              row.key === "Date of First Irrigation"
                                ? "firstIrrigationDate"
                                : "lastIrrigationDate"
                            ] || ""
                          }
                          InputProps={{
                            sx: {
                              height: "40px",
                              borderRadius: "5px",
                              marginTop: "10px",
                              background: "#FFF",
                              border: "1px solid #C7BDBD",
                            },
                            readOnly: true,
                            onClick: () =>
                              document
                                .getElementById(
                                  row.key === "Date of First Irrigation"
                                    ? "first-irrigation-picker"
                                    : "last-irrigation-picker"
                                )
                                .click(),
                          }}
                        />
                        <DatePicker
                          id={
                            row.key === "Date of First Irrigation"
                              ? "first-irrigation-picker"
                              : "last-irrigation-picker"
                          } // Using row.key instead of row.label
                          dateFormat="dd/MM/yyyy"
                          selected={
                            row.key === "Date of First Irrigation"
                              ? firstIrrigationDate
                              : lastIrrigationDate
                          } // Using row.key instead of row.label
                          onChange={(date) =>
                            handleDateChange(
                              date,
                              row.key === "Date of First Irrigation"
                                ? "firstIrrigationDate"
                                : "lastIrrigationDate"
                            )
                          } // Using row.key instead of row.label
                          className={classes.datepicker}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <TextField
                      type="number"
                      disabled={!isEditMode && existingData !== null}
                      value={formData[row.label.toLowerCase()]}
                      onChange={(event) =>
                        handleChange(
                          row.label.toLowerCase(),
                          event.target.value
                        )
                      }
                      size="small"
                      InputProps={{
                        sx: {
                          height: "40px",
                          borderRadius: "5px",
                          marginTop: "10px",
                          background: "#FFF",
                          border: "1px solid #C7BDBD",
                        },
                      }}
                    />
                  )}
                </Grid>
              </Grid>
            ))}
          </div>
        </div>
        <div style={{ display: "grid", padding: "10px" }}>
          <InputLabel>{t("Comment (Optional)")}</InputLabel>
          <TextField
            multiline
            rows={1}
            value={comment}
            onChange={handleCommentChange}
            disabled={!isEditMode && existingData !== null}
          />
        </div>

        <div
          style={{ display: "flex", margin: "auto", justifyContent: "center" }}
        >
          {existingData && isEditMode ? (
            <div
              style={{
                display: "flex",
                margin: "auto",
                justifyContent: "center",
                width: "140px",
              }}
            >
              <CustomButton onClick={handleSubmit} hideIcon>
                {t("Update")}
              </CustomButton>
            </div>
          ) : !existingData && !isEditMode ? (
            <div
              style={{
                display: "flex",
                margin: "auto",
                justifyContent: "center",
                width: "140px",
              }}
            >
              <CustomButton onClick={handleSubmit} hideIcon>
                {t("Submit")}
              </CustomButton>
            </div>
          ) : null}
        </div>
        {showSuccessPopup && (
          <SuccessPopup onClose={() => setShowSuccessPopup(false)} />
        )}
      </Dialog>
    </div>
  );
};

export default IrrigationPopup;
